import { useQuery } from "@tanstack/react-query";
import { useProducts } from "../admin/products/useProducts";
import { listECommerceOffers } from "./eCommerceApi";
import type { ECommerceOfferProductWithNames, ECommerceOfferWithNames } from "./eCommerceTypes";

interface UseECommerceOffersResult {
  readonly offers: ECommerceOfferWithNames[];
  readonly isLoading: boolean;
}

export const useEcommerceOffers = (params: { from: string; to?: string }): UseECommerceOffersResult => {
  const { data, isLoading: areOffersLoading } = useQuery({
    queryKey: ["e-commerce", params],
    queryFn: ({ signal }) => listECommerceOffers(params, signal),
  });
  const { products, isLoading: areProductsLoading } = useProducts();

  const offers: ECommerceOfferWithNames[] = (data ?? []).map((offer) => ({
    ...offer,
    products: offer.products
      .map((product): ECommerceOfferProductWithNames | null => {
        const matchingProduct = products.find((pr) => pr.id === product.id);
        if (!matchingProduct) {
          return null;
        }
        return {
          ...product,
          name: matchingProduct.name,
          providerName: matchingProduct.provider.name,
          options: product.options.map((option) => ({
            ...option,
            name: matchingProduct.options.find((matchingOption) => matchingOption.id === option.id)?.name,
          })),
          description: matchingProduct.description,
          contact: matchingProduct.contact,
          images: matchingProduct.images ?? [],
        };
      })
      .filter((value): value is ECommerceOfferProductWithNames => value !== null),
  }));

  return { offers, isLoading: areOffersLoading || areProductsLoading };
};
